import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';

export const MagicLoginPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    const exchangeToken = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid login link. Please request a new one.');
        return;
      }

      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const response = await fetch(`${baseUrl}/auth/login-with-token?token=${token}`);

        if (!response.ok) {
          throw new Error('Login link is invalid or has expired.');
        }

        const data = await response.json();

        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Don't trust tenants on the token-exchange response directly (it can
        // omit memberships the way other auth endpoints do) — re-fetch the full
        // user record first, matching the pattern in authProvider/ResetPasswordPage.
        let userWithTenants = data.user;
        try {
          const userResponse = await fetch(`${baseUrl}/users/${data.user.id}`, {
            headers: {
              Authorization: `Bearer ${data.accessToken}`,
            },
          });
          if (userResponse.ok) {
            userWithTenants = await userResponse.json();
            localStorage.setItem('user', JSON.stringify(userWithTenants));
          }
        } catch (fetchError) {
          console.warn('Failed to fetch user details after magic-link login:', fetchError);
        }

        const hasTenants = userWithTenants.tenants && userWithTenants.tenants.length > 0;
        if (!hasTenants) {
          localStorage.setItem('isStandalone', 'true');
          window.location.href = '/standalone-dashboard';
          return;
        }

        localStorage.removeItem('isStandalone');
        localStorage.setItem('currentTenant', JSON.stringify(userWithTenants.tenants[0].tenant));
        window.location.href = '/';
      } catch (error: any) {
        setStatus('error');
        setMessage(error.message || 'Login link is invalid or has expired.');
      }
    };

    exchangeToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      {status === 'loading' && (
        <>
          <CircularProgress />
          <Typography>Logging you in...</Typography>
        </>
      )}
      {status === 'error' && (
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {message}
        </Alert>
      )}
    </Box>
  );
};

export default MagicLoginPage;
